import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import DynamicFieldForm from "@/components/DynamicFieldForm";
import { ATTRIBUTES } from "@/constants/contentTestIds";

/**
 * Edit one Location: core fields + additional attributes (driven by the
 * brand's location attribute schema).
 */
export default function LocationDetail() {
  const { brandId, locationId } = useParams();
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [schema, setSchema] = useState({ fields: [] });
  const [attrValues, setAttrValues] = useState({});
  const [coreEditing, setCoreEditing] = useState(false);
  const [coreDraft, setCoreDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    const [{ data: l }, { data: s }, { data: v }] = await Promise.all([
      api.get(`/brands/${brandId}/locations`).then((r) => ({ data: r.data.find((x) => x.location_id === locationId) })),
      api.get(`/brands/${brandId}/attribute-schemas/location`),
      api.get(`/brands/${brandId}/attribute-values/location/${locationId}`),
    ]);
    setLocation(l);
    setCoreDraft(l || {});
    setSchema(s);
    setAttrValues(v.values || {});
  };
  useEffect(() => { load(); }, [locationId]);

  const saveAttributes = async () => {
    setSaving(true);
    setError("");
    try {
      await api.put(`/brands/${brandId}/attribute-values/location/${locationId}`, { values: attrValues });
      setSavedAt(new Date().toISOString());
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!location) return <p className="text-[#6B6358]">Loading&hellip;</p>;

  return (
    <div data-testid={ATTRIBUTES.valuesRoot} className="max-w-4xl">
      <button onClick={() => navigate(`/brands/${brandId}`)} className="text-xs text-[#6B6358]">
        &larr; Back to brand
      </button>
      <p className="text-xs uppercase tracking-[0.22em] text-[#B5462F] mt-4 mb-2">Location</p>
      <h1 className="font-serif text-5xl tracking-tight">{location.name}</h1>
      {location.location_code && (
        <p className="font-mono text-xs text-[#6B6358] mt-2">Code: {location.location_code}</p>
      )}

      <Section title="Core fields">
        <Kvs
          rows={[
            ["Address", [location.address_line1, location.address_line2].filter(Boolean).join(", ")],
            ["City",    location.city],
            ["State",   location.state],
            ["Postal",  location.postal_code],
            ["Country", location.country],
            ["Phone",   location.phone],
          ]}
        />
      </Section>

      <Section title={`Additional attributes · ${schema.fields?.length || 0} fields`}>
        {(schema.fields?.length || 0) === 0 ? (
          <p className="text-sm text-[#6B6358]">
            No additional attributes defined for this brand yet.&nbsp;
            <a className="underline" href={`/brands/${brandId}/attributes`}>Define them</a>.
          </p>
        ) : (
          <>
            <div className="border border-[#1A1F2C]/10 bg-white rounded-lg p-6">
              <DynamicFieldForm
                fields={schema.fields}
                values={attrValues}
                onChange={setAttrValues}
                brandId={brandId}
                testidPrefix={ATTRIBUTES.valueFieldPrefix}
              />
            </div>
            <div className="flex items-center gap-3 mt-6">
              <button
                data-testid={ATTRIBUTES.valuesSave}
                onClick={saveAttributes}
                disabled={saving}
                className="px-5 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm font-medium disabled:opacity-40"
              >
                {saving ? "Saving..." : "Save attributes"}
              </button>
              {savedAt && <span className="text-xs text-[#6B6358]">Saved.</span>}
              {error && <span className="text-sm text-[#B5462F]">{error}</span>}
            </div>
          </>
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-serif text-xl mb-4">{title}</h2>
      {children}
    </section>
  );
}

function Kvs({ rows }) {
  return (
    <div className="border border-[#1A1F2C]/10 bg-white rounded-lg divide-y divide-[#1A1F2C]/10">
      {rows.filter(([, v]) => v).map(([k, v]) => (
        <div key={k} className="px-5 py-3 grid grid-cols-3 text-sm">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6358]">{k}</div>
          <div className="col-span-2">{v}</div>
        </div>
      ))}
    </div>
  );
}
